import React, { useEffect, useState, useMemo } from "react";
import axios from "../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";

/* ─── helpers ─────────────────────────────────────────── */

function toPersian(isoDate) {
  const d = new DateObject({ date: new Date(isoDate), calendar: gregorian, locale: gregorian_en });
  d.convert(persian, persian_fa);
  return d;
}

function persianMonthStart(year, month) {
  const d = new DateObject({ calendar: persian, locale: persian_fa, year, month, day: 1 });
  d.convert(gregorian, gregorian_en);
  return d.toDate();
}

function daysInPersianMonth(year, month) {
  if (month <= 6) return 31;
  if (month <= 11) return 30;
  return 30;
}

const PERSIAN_MONTHS = [
  "فروردین","اردیبهشت","خرداد","تیر","مرداد","شهریور",
  "مهر","آبان","آذر","دی","بهمن","اسفند"
];
const PERSIAN_WEEKDAYS = ["ش","ی","د","س","چ","پ","ج"];

/* ─── Component ───────────────────────────────────────── */

export default function SettlementCalendar() {
  const navigate = useNavigate();

  const today = new DateObject({ calendar: persian, locale: persian_fa });
  const [viewYear, setViewYear]   = useState(today.year);
  const [viewMonth, setViewMonth] = useState(today.month);

  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");

  // modal: { dateStr, dayKey, users: [{userId, unsettledGold, unsettledToman, unsettledCount, settledGold, settledToman, settledCount, orderCount}] }
  const [selectedDay, setSelectedDay] = useState(null);
  // tab inside modal: "all" | "unsettled" | "settled"
  const [modalTab, setModalTab] = useState("all");

  /* fetch ─────────────────────────────────────────────── */
  const fetchData = async (year, month) => {
    setLoading(true);
    setError("");
    try {
      const start = persianMonthStart(year, month);
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear  = month === 12 ? year + 1 : year;
      const end = persianMonthStart(nextYear, nextMonth);

      const response = await axios.get("/Settlement", {
        params: { from: start.toISOString(), to: end.toISOString() },
      });
      setSummaries(response.data?.data?.userSummaries || []);
    } catch {
      setError("خطا در دریافت اطلاعات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(viewYear, viewMonth); }, [viewYear, viewMonth]);

  /* aggregate ─────────────────────────────────────────── */
  const dayMap = useMemo(() => {
    const map = {};
    summaries.forEach(({ userId, dailySummaries }) => {
      dailySummaries.forEach((day) => {
        const pd = toPersian(day.date);
        const key = `${pd.year}-${String(pd.month).padStart(2,"0")}-${String(pd.day).padStart(2,"0")}`;
        if (!map[key]) {
          map[key] = {
            totalUnsettledGold: 0, totalUnsettledToman: 0,
            totalSettledGold: 0,   totalSettledToman: 0,
            totalOrderCount: 0,
            users: [],
          };
        }
        map[key].totalUnsettledGold  += day.unsettledGold  || 0;
        map[key].totalUnsettledToman += day.unsettledToman || 0;
        map[key].totalSettledGold    += day.settledGold    || 0;
        map[key].totalSettledToman   += day.settledToman   || 0;
        map[key].totalOrderCount     += day.orderCount     || 0;
        map[key].users.push({
          userId,
          unsettledGold:   day.unsettledGold   || 0,
          unsettledToman:  day.unsettledToman  || 0,
          unsettledCount:  day.unsettledCount  || 0,
          settledGold:     day.settledGold     || 0,
          settledToman:    day.settledToman    || 0,
          settledCount:    day.settledCount    || 0,
          orderCount:      day.orderCount      || 0,
        });
      });
    });
    return map;
  }, [summaries]);

  /* calendar grid ─────────────────────────────────────── */
  const calendarGrid = useMemo(() => {
    const days = daysInPersianMonth(viewYear, viewMonth);
    const firstDay = new DateObject({ calendar: persian, locale: persian_fa, year: viewYear, month: viewMonth, day: 1 });
    const rawWd = firstDay.weekDay.index;
    const offset = (rawWd + 1) % 7;
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);
    return cells;
  }, [viewYear, viewMonth]);

  /* navigation ────────────────────────────────────────── */
  const prevMonth = () => {
    if (viewMonth === 1) { setViewYear(y => y - 1); setViewMonth(12); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 12) { setViewYear(y => y + 1); setViewMonth(1); }
    else setViewMonth(m => m + 1);
  };

  /* day click ─────────────────────────────────────────── */
  const handleDayClick = (day) => {
    if (!day) return;
    const key = `${viewYear}-${String(viewMonth).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    const data = dayMap[key];
    if (!data || data.users.length === 0) return;
    setSelectedDay({ dateStr: `${viewYear}/${viewMonth}/${day}`, dayKey: key, ...data });
    setModalTab("all");
  };

  /* filtered users for modal tab ──────────────────────── */
  const modalUsers = useMemo(() => {
    if (!selectedDay) return [];
    if (modalTab === "unsettled") return selectedDay.users.filter(u => u.unsettledCount > 0);
    if (modalTab === "settled")   return selectedDay.users.filter(u => u.settledCount > 0);
    return selectedDay.users;
  }, [selectedDay, modalTab]);

  /* ─── render ─────────────────────────────────────────── */
  return (
    <div style={S.page}>
      {/* header */}
      <div style={S.header}>
        <button style={S.backBtn} onClick={() => navigate("/dashboard")}>
          ← بازگشت به داشبورد
        </button>
        <div>
          <h2 style={S.title}>تقویم تسویه سفارشات</h2>
          <p style={S.subtitle}>وضعیت سفارشات تسویه‌شده و تسویه‌نشده به تفکیک روز</p>
        </div>
      </div>

      {error && <p style={S.error}>{error}</p>}

      {/* calendar card */}
      <div style={S.card}>
        {/* month nav */}
        <div style={S.monthNav}>
          <button style={S.navBtn} onClick={nextMonth}>{"›"}</button>
          <h3 style={S.monthTitle}>{PERSIAN_MONTHS[viewMonth - 1]} {viewYear}</h3>
          <button style={S.navBtn} onClick={prevMonth}>{"‹"}</button>
        </div>

        {loading ? (
          <div style={S.loadingBox}>در حال بارگذاری...</div>
        ) : (
          <div style={S.grid}>
            {/* weekday headers */}
            {PERSIAN_WEEKDAYS.map((wd) => (
              <div key={wd} style={S.weekdayCell}>{wd}</div>
            ))}

            {/* day cells */}
            {calendarGrid.map((day, idx) => {
              if (!day) return <div key={`e-${idx}`} style={S.emptyCell} />;

              const key = `${viewYear}-${String(viewMonth).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const data = dayMap[key];
              const hasUnsettled = data && (data.totalUnsettledGold > 0 || data.totalUnsettledToman > 0);
              const hasSettled   = data && (data.totalSettledGold   > 0 || data.totalSettledToman   > 0);
              const hasAny = hasUnsettled || hasSettled;

              const isToday = today.year === viewYear && today.month === viewMonth && today.day === day;

              return (
                <div
                  key={day}
                  style={{
                    ...S.dayCell,
                    ...(isToday ? S.todayCell : {}),
                    ...(hasAny ? S.hasDataCell : {}),
                    cursor: hasAny ? "pointer" : "default",
                  }}
                  onClick={() => handleDayClick(day)}
                >
                  <span style={S.dayNumber}>{day}</span>
                  {hasAny && (
                    <div style={S.badges}>
                      {/* total orders */}
                      <span style={S.orderBadge} title="کل سفارشات">
                        📦 {data.totalOrderCount}
                      </span>
                      {/* unsettled gold */}
                      {data.totalUnsettledGold > 0 && (
                        <span style={S.unsettledGoldBadge} title="طلای تسویه‌نشده">
                          🟠 {data.totalUnsettledGold}
                        </span>
                      )}
                      {/* unsettled toman */}
                      {data.totalUnsettledToman > 0 && (
                        <span style={S.unsettledTomanBadge} title="تومان تسویه‌نشده">
                          🔴 {Number(data.totalUnsettledToman).toLocaleString()}
                        </span>
                      )}
                      {/* settled gold */}
                      {data.totalSettledGold > 0 && (
                        <span style={S.settledGoldBadge} title="طلای تسویه‌شده">
                          🟢 {data.totalSettledGold}
                        </span>
                      )}
                      {/* settled toman */}
                      {data.totalSettledToman > 0 && (
                        <span style={S.settledTomanBadge} title="تومان تسویه‌شده">
                          💚 {Number(data.totalSettledToman).toLocaleString()}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* legend */}
      <div style={S.legend}>
        <span style={S.legendItem}><span style={{...S.dot, background:"#f97316"}}/>طلای تسویه‌نشده</span>
        <span style={S.legendItem}><span style={{...S.dot, background:"#ef4444"}}/>تومان تسویه‌نشده</span>
        <span style={S.legendItem}><span style={{...S.dot, background:"#22c55e"}}/>طلای تسویه‌شده</span>
        <span style={S.legendItem}><span style={{...S.dot, background:"#16a34a"}}/>تومان تسویه‌شده</span>
        <span style={S.legendItem}><span style={{...S.dot, background:"#6366f1", border:"2px solid #4f46e5"}}/>امروز</span>
      </div>

      {/* ── MODAL ── */}
      {selectedDay && (
        <div style={S.overlay} onClick={() => setSelectedDay(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>

            {/* modal header */}
            <div style={S.modalHeader}>
              <h3 style={S.modalTitle}>جزئیات روز {selectedDay.dateStr}</h3>
              <button style={S.closeBtn} onClick={() => setSelectedDay(null)}>✕</button>
            </div>

            {/* summary chips */}
            <div style={S.modalSummary}>
              <div style={S.chip}>
                <span style={S.chipLabel}>📦 تعداد کل سفارش ها : </span>
                <strong style={S.chipValue}>{selectedDay.totalOrderCount}</strong>
              </div>
              {(selectedDay.totalUnsettledGold > 0 || selectedDay.totalUnsettledToman > 0) && (
                <div style={{...S.chip, borderColor:"#fdba74", background:"#fff7ed"}}>
                  <span style={S.chipLabel}>🟠 مقدار طلای تسویه نشده (گرم)</span>
                  <strong style={{...S.chipValue, color:"#c2410c"}}>{selectedDay.totalUnsettledGold}</strong>
                </div>
              )}
              {selectedDay.totalUnsettledToman > 0 && (
                <div style={{...S.chip, borderColor:"#fca5a5", background:"#fff1f2"}}>
                  <span style={S.chipLabel}>🔴 مقدار تومان تسویه نشده : </span>
                  <strong style={{...S.chipValue, color:"#b91c1c"}}>{Number(selectedDay.totalUnsettledToman).toLocaleString()}</strong>
                </div>
              )}
              {(selectedDay.totalSettledGold > 0 || selectedDay.totalSettledToman > 0) && (
                <div style={{...S.chip, borderColor:"#86efac", background:"#f0fdf4"}}>
                  <span style={S.chipLabel}>🟢 مقدار طلای تسویه شده ( گرم ) : </span>
                  <strong style={{...S.chipValue, color:"#15803d"}}>{selectedDay.totalSettledGold}</strong>
                </div>
              )}
              {selectedDay.totalSettledToman > 0 && (
                <div style={{...S.chip, borderColor:"#6ee7b7", background:"#ecfdf5"}}>
                  <span style={S.chipLabel}>💚 مقدار تومان تسویه نشده</span>
                  <strong style={{...S.chipValue, color:"#065f46"}}>{Number(selectedDay.totalSettledToman).toLocaleString()}</strong>
                </div>
              )}
            </div>

            {/* tabs */}
            <div style={S.tabs}>
              {[
                { key:"all",       label:"همه کاربران" },
                { key:"unsettled", label:"🟠 تسویه‌نشده" },
                { key:"settled",   label:"🟢 تسویه‌شده" },
              ].map(t => (
                <button
                  key={t.key}
                  style={{ ...S.tab, ...(modalTab === t.key ? S.tabActive : {}) }}
                  onClick={() => setModalTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* user table */}
            {modalUsers.length === 0 ? (
              <p style={S.emptyMsg}>داده‌ای برای نمایش وجود ندارد</p>
            ) : (
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>کاربر</th>
                    <th style={S.th}>کل سفارش</th>
                    <th style={S.th} colSpan={2}>تسویه‌نشده</th>
                    <th style={S.th} colSpan={2}>تسویه‌شده</th>
                  </tr>
                  <tr>
                    <th style={S.th2}></th>
                    <th style={S.th2}></th>
                    <th style={{...S.th2, color:"#c2410c"}}>طلا</th>
                    <th style={{...S.th2, color:"#b91c1c"}}>تومان</th>
                    <th style={{...S.th2, color:"#15803d"}}>طلا</th>
                    <th style={{...S.th2, color:"#065f46"}}>تومان</th>
                  </tr>
                </thead>
                <tbody>
                  {modalUsers.map((u, i) => (
                    <tr key={u.userId} style={i % 2 === 0 ? S.rowEven : S.rowOdd}>
                      <td style={S.td}>{u.userId}</td>
                      <td style={S.td}>{u.orderCount}</td>
                      <td style={{...S.td, color: u.unsettledGold > 0 ? "#c2410c" : "#94a3b8"}}>
                        {u.unsettledGold > 0 ? u.unsettledGold : "—"}
                      </td>
                      <td style={{...S.td, color: u.unsettledToman > 0 ? "#b91c1c" : "#94a3b8"}}>
                        {u.unsettledToman > 0 ? Number(u.unsettledToman).toLocaleString() : "—"}
                      </td>
                      <td style={{...S.td, color: u.settledGold > 0 ? "#15803d" : "#94a3b8"}}>
                        {u.settledGold > 0 ? u.settledGold : "—"}
                      </td>
                      <td style={{...S.td, color: u.settledToman > 0 ? "#065f46" : "#94a3b8"}}>
                        {u.settledToman > 0 ? Number(u.settledToman).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── styles ─────────────────────────────────────────── */
const S = {
  page: {
    direction: "rtl",
    fontFamily: "Tahoma, Arial, sans-serif",
    minHeight: "100vh",
    background: "#f1f5f9",
    padding: "24px 16px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "24px",
    maxWidth: "960px",
    margin: "0 auto 24px",
  },
  backBtn: {
    padding: "8px 16px",
    background: "#334155",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  title: { margin: 0, fontSize: "22px", color: "#1e293b", fontWeight: "700" },
  subtitle: { margin: "4px 0 0", fontSize: "13px", color: "#64748b" },
  error: {
    background: "#fee2e2", color: "#b91c1c",
    padding: "10px 16px", borderRadius: "8px", marginBottom: "16px",
  },
  card: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    padding: "24px",
    maxWidth: "960px",
    margin: "0 auto",
  },
  monthNav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
  navBtn: {
    width: "36px", height: "36px", border: "none", borderRadius: "50%",
    background: "#e2e8f0", color: "#334155", fontSize: "22px", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold",
  },
  monthTitle: { margin: 0, fontSize: "20px", fontWeight: "700", color: "#1e293b" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "5px",
  },
  weekdayCell: {
    textAlign: "center", fontWeight: "700", fontSize: "13px",
    color: "#64748b", padding: "8px 0", borderBottom: "2px solid #f1f5f9",
  },
  emptyCell: { minHeight: "100px" },
  dayCell: {
    minHeight: "100px",
    borderRadius: "10px",
    border: "1px solid #e2e8f0",
    padding: "6px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "3px",
    transition: "box-shadow 0.15s",
    background: "#fafafa",
  },
  todayCell: { border: "2px solid #6366f1", background: "#eef2ff" },
  hasDataCell: { background: "#fff", border: "1px solid #cbd5e1", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  dayNumber: { fontSize: "13px", fontWeight: "600", color: "#374151" },
  badges: { display: "flex", flexDirection: "column", gap: "2px", width: "100%" },
  orderBadge: {
    fontSize: "10px", background: "#f1f5f9", color: "#475569",
    borderRadius: "4px", padding: "2px 4px", display: "block", textAlign: "center", fontWeight: "600",
  },
  unsettledGoldBadge: {
    fontSize: "10px", background: "#fff7ed", color: "#c2410c",
    borderRadius: "4px", padding: "2px 4px", display: "block", textAlign: "center", fontWeight: "600",
  },
  unsettledTomanBadge: {
    fontSize: "10px", background: "#fff1f2", color: "#b91c1c",
    borderRadius: "4px", padding: "2px 4px", display: "block", textAlign: "center", fontWeight: "600",
  },
  settledGoldBadge: {
    fontSize: "10px", background: "#f0fdf4", color: "#15803d",
    borderRadius: "4px", padding: "2px 4px", display: "block", textAlign: "center", fontWeight: "600",
  },
  settledTomanBadge: {
    fontSize: "10px", background: "#ecfdf5", color: "#065f46",
    borderRadius: "4px", padding: "2px 4px", display: "block", textAlign: "center", fontWeight: "600",
  },
  legend: {
    display: "flex", gap: "16px", justifyContent: "center",
    marginTop: "16px", flexWrap: "wrap", maxWidth: "960px", margin: "16px auto 0",
  },
  legendItem: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#475569" },
  dot: { width: "11px", height: "11px", borderRadius: "50%", display: "inline-block", flexShrink: 0 },
  loadingBox: { textAlign: "center", padding: "60px 0", color: "#64748b", fontSize: "16px" },

  /* modal */
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999, padding: "16px",
  },
  modal: {
    background: "#fff", borderRadius: "16px", padding: "24px",
    width: "100%", maxWidth: "680px", maxHeight: "85vh", overflowY: "auto",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)", direction: "rtl",
  },
  modalHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px",
  },
  modalTitle: { margin: 0, fontSize: "18px", color: "#1e293b", fontWeight: "700" },
  closeBtn: { background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#64748b", lineHeight: 1 },
  modalSummary: { display: "flex", gap: "10px", marginBottom: "16px", flexWrap: "wrap" },
  chip: {
    border: "1px solid #e2e8f0", background: "#f8fafc",
    borderRadius: "8px", padding: "8px 12px",
    display: "flex", flexDirection: "column", gap: "2px", minWidth: "90px",
  },
  chipLabel: { fontSize: "11px", color: "#64748b" },
  chipValue: { fontSize: "15px", color: "#1e293b" },

  /* tabs */
  tabs: { display: "flex", gap: "8px", marginBottom: "14px", borderBottom: "2px solid #f1f5f9", paddingBottom: "8px" },
  tab: {
    padding: "6px 14px", border: "none", borderRadius: "6px",
    background: "#f1f5f9", color: "#64748b", cursor: "pointer", fontSize: "13px", fontWeight: "600",
  },
  tabActive: { background: "#334155", color: "#fff" },
  emptyMsg: { textAlign: "center", color: "#94a3b8", padding: "24px 0", fontSize: "14px" },

  /* table */
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px" },
  th: {
    background: "#f1f5f9", padding: "8px 10px", textAlign: "center",
    fontWeight: "700", color: "#475569", borderBottom: "2px solid #e2e8f0",
  },
  th2: {
    background: "#f8fafc", padding: "6px 10px", textAlign: "center",
    fontWeight: "600", fontSize: "12px", color: "#64748b", borderBottom: "1px solid #e2e8f0",
  },
  td: { padding: "9px 10px", textAlign: "center", color: "#374151", borderBottom: "1px solid #f1f5f9" },
  rowEven: { background: "#fff" },
  rowOdd:  { background: "#f8fafc" },
};
